import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

type Provider = "google" | "apple";

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleIdApi = {
  initialize: (options: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
};

type GoogleIdentityWindow = {
  google?: { accounts?: { id?: GoogleIdApi } };
};

type AppleAuthorizationResponse = {
  authorization?: {
    id_token?: string;
  };
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (options: Record<string, unknown>) => void;
        signIn: () => Promise<AppleAuthorizationResponse>;
      };
    };
  }
}

type Props = {
  disabled?: boolean;
  onToken: (provider: Provider, token: string) => Promise<void>;
  onError: (error: Error) => void;
};

const GOOGLE_SCRIPT = "https://accounts.google.com/gsi/client";
const APPLE_SCRIPT = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const scriptPromises = new Map<string, Promise<void>>();

function loadScript(source: string) {
  const existingPromise = scriptPromises.get(source);
  if (existingPromise) return existingPromise;

  const promise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${source}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Could not load the sign-in provider.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = source;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load the sign-in provider."));
    document.head.appendChild(script);
  });

  scriptPromises.set(source, promise);
  return promise;
}

export default function SocialSignInButtons({ disabled = false, onToken, onError }: Props) {
  const googleButton = useRef<HTMLDivElement>(null);
  const submitted = useRef(false);
  const onTokenRef = useRef(onToken);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID?.trim();
  const appleServiceId = import.meta.env.VITE_APPLE_SERVICE_ID?.trim();
  const appleRedirectUri = import.meta.env.VITE_APPLE_OAUTH_REDIRECT_URI?.trim() || `${window.location.origin}/auth-callback`;

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  const submitToken = useCallback(async (provider: Provider, token?: string) => {
    if (!token || submitted.current) return;

    submitted.current = true;
    try {
      await onTokenRef.current(provider, token);
    } finally {
      submitted.current = false;
    }
  }, []);

  useEffect(() => {
    if (!googleClientId || !googleButton.current) {
      setGoogleUnavailable(true);
      return;
    }

    let cancelled = false;
    void loadScript(GOOGLE_SCRIPT)
      .then(() => {
        const googleId = (window as Window & GoogleIdentityWindow).google?.accounts?.id;
        if (cancelled || !googleButton.current || !googleId) return;

        googleId.initialize({
          client_id: googleClientId,
          callback: (response) => void submitToken("google", response.credential),
        });
        googleButton.current.replaceChildren();
        googleId.renderButton(googleButton.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: Math.min(400, Math.max(260, Math.floor(googleButton.current.clientWidth))),
        });
      })
      .catch(() => {
        if (!cancelled) setGoogleUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [googleClientId, submitToken]);

  const handleAppleSignIn = async () => {
    if (disabled || appleLoading || submitted.current) return;
    setAppleLoading(true);
    try {
      if (!appleServiceId) {
        throw new Error("Apple sign-in has not been configured for this website.");
      }
      await loadScript(APPLE_SCRIPT);
      if (!window.AppleID?.auth) {
        throw new Error("Apple sign-in could not be initialized.");
      }

      window.AppleID.auth.init({
        clientId: appleServiceId,
        scope: "name email",
        redirectURI: appleRedirectUri,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      if (!response.authorization?.id_token) {
        throw new Error("Apple did not return a sign-in token. Please try again.");
      }
      await submitToken("apple", response.authorization?.id_token);
    } catch (error) {
      onError(error instanceof Error ? error : new Error("Apple sign-in failed. Please try again."));
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className="w-full mt-6">
      <div className="flex items-center gap-3 text-light-3 text-xs">
        <span className="h-px flex-1 bg-dark-4" />
        <span>or continue with</span>
        <span className="h-px flex-1 bg-dark-4" />
      </div>

      <div className="flex flex-col gap-4 mt-4 w-full max-w-[400px] mx-auto">
        <div className="w-full min-h-11 rounded-lg" ref={googleButton}>
          {googleUnavailable && (
            <Button type="button" className="w-full h-11" variant="secondary" disabled>
              Google unavailable
            </Button>
          )}
        </div>
        <Button
          type="button"
          className="h-11 bg-white text-black hover:bg-white/90"
          disabled={disabled || appleLoading}
          onClick={() => void handleAppleSignIn()}
        >
          {appleLoading ? "Connecting..." : "Apple"}
        </Button>
      </div>
    </div>
  );
}
